import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import { Search, Filter, RefreshCw } from 'lucide-react';

/**
 * AuditPage — manager/director audit history (Feb 2026 upgrade).
 *
 * The spec says: every edit/void/deletion/adjustment must record User,
 * Date, Time, Reason. This page surfaces that history with action +
 * user + date filters, a free-text search, and a prominent reason
 * column so the manager can scan voids/refunds/merges at a glance.
 */

// Colour map kept narrow on purpose — anything not in here renders as
// neutral zinc. Add a row when a new high-signal action lands.
const actionColors = {
  // Auth + staff
  staff_login: 'text-blue-accent',
  staff_logout: 'text-blue-accent',
  staff_registered: 'text-blue-accent',
  staff_deleted: 'text-red-400',
  // Orders
  order_created: 'text-emerald-400',
  order_voided: 'text-red-400',
  order_hard_deleted: 'text-red-500',
  order_edited: 'text-amber-400',
  item_status_updated: 'text-zinc-400',
  // Payments + tabs
  cash_payment_received: 'text-gold',
  tab_settled: 'text-gold',
  tab_settled_via_partials: 'text-gold',
  tab_auto_day_closed: 'text-amber-400',
  tabs_merged: 'text-amber-300',
  // Tables
  table_status_changed: 'text-blue-accent',
  table_status_cleaning: 'text-orange-300',
  table_created: 'text-emerald-400',
  // Menu / inventory
  menu_item_created: 'text-amber-400',
  menu_item_updated: 'text-amber-400',
  menu_item_deleted: 'text-red-400',
  stock_adjusted: 'text-purple-400',
  inventory_item_created: 'text-blue-accent',
  // Till
  till_opened: 'text-emerald-400',
  till_closed: 'text-amber-400',
  // Misc
  po_created: 'text-gold',
  po_received: 'text-emerald-400',
  performance_scored: 'text-gold',
  feedback_submitted: 'text-emerald-400',
  damage_claimed: 'text-red-400',
  damage_approved: 'text-emerald-400',
  location_created: 'text-blue-accent',
  offline_orders_synced: 'text-amber-400',
};

// Detail keys that, when present, are a "reason" — surfaced as a separate
// column on the row. Different endpoints used different field names
// historically; we treat them all as reasons here.
const REASON_KEYS = [
  'reason',
  'void_reason',
  'force_reason',
  'merge_reason',
  'edit_reason',
  'delete_reason',
  'cancellation_reason',
];

function extractReason(details) {
  if (!details || typeof details !== 'object') return '';
  for (const k of REASON_KEYS) {
    if (details[k]) return String(details[k]);
  }
  return '';
}

// Pick a small summary string from the rest of the details — keeps the
// row scannable without dumping the whole JSON blob in the customer's
// face. We blacklist the reason keys (shown separately) and a couple of
// noisy internal ids.
const SUMMARY_BLACKLIST = new Set([...REASON_KEYS, 'forced_actions', 'order_ids', 'partials_paid']);

function summariseDetails(details) {
  if (!details || typeof details !== 'object') return '';
  const parts = [];
  for (const [k, v] of Object.entries(details)) {
    if (SUMMARY_BLACKLIST.has(k)) continue;
    if (v === null || v === undefined || v === '') continue;
    const valStr = typeof v === 'object' ? JSON.stringify(v) : String(v);
    parts.push(`${k}: ${valStr}`);
  }
  return parts.join(' · ').slice(0, 160);
}

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState({});  // id → name lookup
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [limit, setLimit] = useState(300);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit-logs?limit=${limit}`);
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[AuditPage.js] fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Best-effort user name lookup so the row shows "Yogesh" instead of
    // a UUID. Non-fatal if it 403s — falls back to the raw id.
    (async () => {
      try {
        const { data } = await api.get('/staff');
        const lookup = {};
        (data || []).forEach(u => { lookup[u.id] = u.name || u.email; });
        setUsers(lookup);
      } catch { /* ignore */ }
    })();
  }, []);

  const actionOptions = useMemo(() => {
    const set = new Set(logs.map(l => l.action).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [logs]);

  const userOptions = useMemo(() => {
    const set = new Set(logs.map(l => l.user_id).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter(l => {
      if (filterAction !== 'all' && l.action !== filterAction) return false;
      if (filterUser !== 'all' && l.user_id !== filterUser) return false;
      if (!q) return true;
      const haystack = [
        l.action,
        users[l.user_id] || l.user_id,
        extractReason(l.details),
        summariseDetails(l.details),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [logs, search, filterAction, filterUser, users]);

  return (
    <div className="animate-fadeIn space-y-4" data-testid="audit-page">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-white/[0.02] border border-white/10 rounded-sm p-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search action, user, reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-sm font-sans text-white placeholder-zinc-600 flex-1"
            data-testid="audit-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="bg-bg-primary border border-white/10 rounded-sm px-2 py-1 text-xs font-sans text-white"
            data-testid="audit-filter-action"
          >
            {actionOptions.map(a => (
              <option key={a} value={a}>{a === 'all' ? 'All actions' : a.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="bg-bg-primary border border-white/10 rounded-sm px-2 py-1 text-xs font-sans text-white max-w-[180px]"
            data-testid="audit-filter-user"
          >
            {userOptions.map(u => (
              <option key={u} value={u}>
                {u === 'all' ? 'All users' : (users[u] || u.slice(0, 8))}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="bg-bg-primary border border-white/10 rounded-sm px-2 py-1 text-xs font-sans text-white"
            data-testid="audit-limit"
          >
            {[100, 300, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="px-3 py-1.5 bg-gold/10 text-gold hover:bg-gold/20 border border-gold/30 rounded-sm font-sans text-xs uppercase tracking-[0.12em] font-semibold flex items-center gap-1.5 disabled:opacity-50"
          data-testid="audit-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <p className="text-xs font-sans text-zinc-500" data-testid="audit-count">
        {filtered.length} of {logs.length} entries
        {filterAction !== 'all' && <> · action: <span className="text-zinc-300">{filterAction}</span></>}
        {filterUser !== 'all' && <> · user: <span className="text-zinc-300">{users[filterUser] || filterUser.slice(0, 8)}</span></>}
      </p>

      {/* Header row */}
      <div className="hidden md:grid grid-cols-[180px_180px_180px_1fr_220px] gap-3 px-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans font-bold">
        <span>Timestamp</span>
        <span>Action</span>
        <span>User</span>
        <span>Reason</span>
        <span>Details</span>
      </div>

      <div className="space-y-1">
        {filtered.map(log => {
          const reason = extractReason(log.details);
          const summary = summariseDetails(log.details);
          const userName = users[log.user_id] || (log.user_id ? log.user_id.slice(0, 8) : 'system');
          return (
            <div
              key={log.id}
              className="grid grid-cols-1 md:grid-cols-[180px_180px_180px_1fr_220px] gap-3 p-2.5 bg-white/[0.02] border border-white/5 rounded-sm hover:bg-white/[0.04] transition-colors"
              data-testid={`audit-log-${log.id}`}
            >
              <span className="text-[11px] text-zinc-500 font-mono whitespace-nowrap">
                {new Date(log.created_at).toLocaleString()}
              </span>
              <span className={`text-xs font-sans font-medium ${actionColors[log.action] || 'text-zinc-400'}`} data-testid={`audit-action-${log.id}`}>
                {(log.action || '').replace(/_/g, ' ')}
              </span>
              <span className="text-xs font-sans text-zinc-300 truncate" title={log.user_id || 'system'}>{userName}</span>
              <span className={`text-xs font-sans ${reason ? 'text-amber-200' : 'text-zinc-600 italic'}`} data-testid={`audit-reason-${log.id}`}>
                {reason || '—'}
              </span>
              <span className="text-[11px] text-zinc-500 font-mono truncate" title={summary}>{summary}</span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-zinc-500 font-sans text-center py-8" data-testid="audit-empty">
            {logs.length === 0 ? 'No audit logs yet' : 'No entries match the current filter.'}
          </p>
        )}
      </div>
    </div>
  );
}
